import * as hubspot from 'my-core/analytics/hubspot';
import { markHuman, whenHuman } from 'my-core/analytics/human-gate';
import * as posthog from 'my-core/analytics/posthog';
import { signupPage } from 'my-core/analytics/signup-page';
import { getPackagePricing, isSubscriptionOffering } from 'my-core/online-course-utils';
import {
  CustomerIO,
  dataLayer,
  fbevents,
  gtag,
  Intercom,
  mixpanel,
  oaiq,
  ShareASale,
  tikTokTrack,
} from 'my-core/tag-manager';
import { debounce } from 'my-utils';

import { isLsatCourse, isMcatCourse } from './course-utils';
import { SUBSCRIPTION_PRICE } from './subscription-utils';

/******************************
 *      GENERIC EVENT         *
 ******************************/

const LOG_EVENTS = window?.localStorage?.getItem('EVENT_TRACK_LOGGING');
export function trackCustomEvent(
  action,
  eventProps = {},
  { beacon, callback: callbackArg, custIO, fb, intercom, sendInstantly = false } = {},
) {
  const callback = !!callbackArg && createCallbackWithTimeout(callbackArg);
  const sendImmediately = sendInstantly || !!callback;
  whenHuman(() => {
    mixpanel.track(
      action,
      eventProps,
      beacon ? { send_immediately: true, transport: 'sendBeacon' } : { send_immediately: sendImmediately },
      callback,
    );
    posthog.capture(action, eventProps, beacon ? { beacon } : { sendInstantly: sendImmediately });
    hubspot.trackEvent(action, eventProps);
    if (intercom) Intercom('trackEvent', action, eventProps);
    if (fb) fbevents('trackCustom', action, eventProps);
    if (custIO) {
      const evName = action.toLowerCase().replace(/( |-)+/g, '_');
      CustomerIO.track(evName, { ...eventProps, ...custIO });
    }

    if (LOG_EVENTS) console.log('Track: ', action, eventProps); // eslint-disable-line no-console
  });
}

function createCallbackWithTimeout(callback, timeout = 1000) {
  let called = false;
  function wrappedCallback(...args) {
    if (!called) {
      called = true;
      callback(...args);
    }
  }
  setTimeout(wrappedCallback, timeout);
  return wrappedCallback;
}

export function trackCoreWebVitals() {
  import('web-vitals').then(({ onCLS, onINP, onLCP }) => {
    // Mixpanel only: PostHog captures web vitals natively as $web_vitals
    function trackCWVMetric(metric) {
      whenHuman(() =>
        mixpanel.track(`CWV - ${metric.name}`, {
          metric_value: metric.value,
          metric_rating: metric.rating,
          metric_delta: metric.delta,
          metric_id: metric.id,
          metric_nav_type: metric.navigationType,
        }),
      );
    }
    onINP(trackCWVMetric);
    onCLS(trackCWVMetric);
    onLCP(trackCWVMetric);
  });
}

export function experimentInitiated(experimentName, variant) {
  trackCustomEvent('Experiment Initiated', { experiment_name: experimentName, experiment_variant: variant });
}

/******************************
 *          APP BAR           *
 ******************************/
export const appSearchPerformed = debounce(query => {
  trackCustomEvent('App Search Performed', { search_string: query });
  fbevents('track', 'Search', { search_string: query });
  gtag('event', 'search', { search_term: query });
}, 2000);

export function appBarNavLinkClicked(label) {
  trackCustomEvent('App Bar Nav Link Clicked', { label });
}

export function chatSupportOpened() {
  trackCustomEvent('Chat Support Opened');
  gtag('event', 'Chat Support Opened');
  fbevents('track', 'Contact');
}

export function notificationClicked(notification) {
  trackCustomEvent('Notification Clicked', {
    notification_type: notification.type,
    notification_notifiable_id: notification.notifiable_id,
    notification_notifiable_type: notification.notifiable_type,
    notification_group_key: notification.group_key,
  });
}
export function siteTourStarted(triggerSource) {
  trackCustomEvent('Site Tour Started', { trigger_source: triggerSource });
}
/********************************
 *   USER ACCOUNT/ONBOARDING     *
 ********************************/

export function userAuthFormViewed(source, isLoginPage) {
  trackCustomEvent('User Auth Form Viewed', { source, page: isLoginPage ? 'login' : 'signup' }, { fb: true });
}
export function userAuthAttempted(method, source, isLoginPage) {
  trackCustomEvent('User Auth Attempted', { method, source, page: isLoginPage ? 'login' : 'signup' });
}
// A finished sign-in is the human input the gate waits for, and the reload that follows would drop a held event.
export function userLogInCompleted(callback, user, method, source) {
  markHuman();
  mixpanel.identify(user.id);
  posthog.identify(user.id);
  hubspot.update(user);
  trackCustomEvent('User Login Completed', { method, source }, { callback });
  gtag('event', 'login', { method });
}

export function userSignUpCompleted(callback, user, method, source) {
  markHuman();
  fbevents('track', 'CompleteRegistration');
  gtag('event', 'sign_up', { method });
  dataLayer.push({ event: 'sign_up' });
  ShareASale.lead(user);
  CustomerIO.identify(user);
  tikTokTrack('CompleteRegistration');
  oaiq(
    'measure',
    'trial_started',
    { type: 'plan_enrollment', plan_id: 'free_account' },
    { event_id: `user-${user.id}` },
  );
  // if user session ended without explicitly logging out (signed in on another device), then this event may be attributed to the
  // previously signed in user. So call "mixpanel.identify" to make sure this event is attributed to new user
  mixpanel.identify(user.id);
  posthog.identify(user.id);
  hubspot.update(user);
  trackCustomEvent('User Sign Up Completed', { method, source, signup_page: signupPage() }, { custIO: true, callback });
}

export function reauthenticationFormViewed() {
  trackCustomEvent('User Reauth Form Viewed');
}
export function googleOneTapSignInStateChanged(opts) {
  // Mixpanel only: fires several times per page load and is noise in PostHog
  whenHuman(() => mixpanel.track('Google One-Tap Sign State Changed', opts));
}

/********************************************************
 *                      CHECKOUT                        *
 *******************************************************/

export function checkoutCardPaymentAttempted() {
  trackCustomEvent('Checkout Card Payment Attempted');
}

export function checkoutExistingCardPaymentAttempted() {
  trackCustomEvent('Checkout Existing Card Payment Attempted');
}
export function checkoutBrowserPaymentEnabled() {
  trackCustomEvent('Checkout Browser Payment Enabled');
}
export function checkoutBrowserPaymentClicked() {
  trackCustomEvent('Checkout Browser Payment Clicked');
}

export function checkoutPaymentAttempted(paymentMethodType) {
  trackCustomEvent('Checkout Payment Attempted', { payment_method_type: paymentMethodType });
}

/********************************************************
 *                     SCHOLARSHIPS                     *
 *******************************************************/
export function scholarshipsLandingPageViewed() {
  trackCustomEvent('Scholarships Landing Page Viewed');
}
export function scholarshipPageViewed(scholarship) {
  trackCustomEvent('Scholarship Page Viewed', {
    scholarship_id: scholarship.id,
    scholarship_name: scholarship.name,
    scholarship_company: scholarship.company_name,
  });
}
export function scholarshipApplyNowClicked(scholarship) {
  trackCustomEvent(
    'Scholarship Apply Now Clicked',
    { scholarship_id: scholarship.id, scholarship_name: scholarship.name },
    { custIO: true },
  );
}

export function scholarshipApplicationCompleted(scholarship) {
  trackCustomEvent('Scholarship Applciation Completed', {
    scholarship_id: scholarship.id,
    scholarship_name: scholarship.name,
  });
}

/********************************************************
 *                     COURSE PAGE                      *
 *******************************************************/

export function coursesPageSearchPerformed({ country, region, schoolId, schoolType, subject }) {
  trackCustomEvent('Courses Page Search Performed', {
    search_country: country,
    search_region: region,
    search_subject: subject,
    search_school_type: schoolType,
    search_school_id: schoolId,
  });
}

export function courseCardClicked(course) {
  trackCustomEvent('Course Card Clicked', {
    course_code: course.code,
    course_name: course.name,
    school_name: course.school?.name || course.school_name,
  });
}

export function coursePageViewed(course, school) {
  trackCustomEvent(
    'Course Page Viewed',
    {
      course_code: course.code,
      course_id: course.id,
      is_high_priority: course.high_priority,
      school_name: school?.name,
      school_id: school?.id,
    },
    { custIO: true, intercom: true },
  );
}

export function coursePageTabClicked(course, school, tab) {
  trackCustomEvent('Course Page Tab Clicked', {
    course_code: course.code,
    course_id: course.id,
    school_name: school?.name,
    school_id: school?.id,
    tab,
  });
}

export function coursePageTabViewed(course, school, tab) {
  trackCustomEvent('Course Page Tab Viewed', {
    course_code: course.code,
    course_id: course.id,
    school_name: school?.name,
    school_id: school?.id,
    tab,
  });
}

export const coursePageOnlineCoursesSearched = debounce((course, query) => {
  trackCustomEvent('Course Page Online Courses Searched', {
    course_code: course.code,
    course_id: course.id,
    school_name: course.school_name,
    school_id: course.school_id,
    query,
  });
}, 3000);

export function studentCourseAdded(course, school) {
  trackCustomEvent(
    'Student Course Added',
    {
      course_code: course.code,
      course_id: course.id,
      school_name: school?.name,
      school_id: school?.id,
    },
    { fb: true },
  );
}

export function courseBookletPageViewed(course, school) {
  trackCustomEvent(
    'Course Booklet Page Viewed',
    {
      course_code: course.code,
      course_id: course.id,
      school_name: school?.name,
      school_id: school?.id,
    },
    { custIO: true },
  );
}

export function onlineCourseBookletRequested(onlineCourseId, path, printParams, bookletGenerated) {
  trackCustomEvent(
    'Online Course Booklet Requested',
    {
      online_course_id: onlineCourseId,
      booklet_path: path,
      print_params: printParams,
      booklet_generated: bookletGenerated,
    },
    { custIO: true },
  );
}
export function onlineCourseBookletBannerLinkClicked() {
  trackCustomEvent('Online Course Booklet Banner Link Clicked');
}

/********************************************************
 *                ONLINE COURSE PURCHASE                *
 *******************************************************/

export function onlineCourseCheckoutStepViewed(onlineCourse, step, packageType) {
  trackCustomEvent('Online Course Checkout Step Viewed', {
    ...getOnlineCourseEventProperties(onlineCourse),
    checkout_step: step,
  });

  if (step === 1) {
    fbevents('track', 'InitiateCheckout', {
      content_category: 'online-course',
      currency: onlineCourse.currency,
      value: onlineCourse.price,
    });
    gtag('event', 'begin_checkout', {
      items: [eecOnlineCourse(onlineCourse, packageType)],
      currency: onlineCourse.currency,
      value: onlineCourse.price,
    });
    CustomerIO.track('product_checkout_viewed', {
      product_id: `OC${onlineCourse.id}`,
      product_type: 'online-course',
      product_price: onlineCourse.price,
      product_name: [onlineCourse.course_code || onlineCourse.course?.code, onlineCourse.name]
        .filter(Boolean)
        .join(': '),
      online_course_type: onlineCourse.course_type,
    });
  } else {
    gtag('event', 'checkout_progress', { items: [eecOnlineCourse(onlineCourse, packageType)], checkout_step: step });
  }
}

export function onlineCoursePurchased(onlineCourse, onlineCourseUser, { discountCode, paymentDetails }) {
  trackCustomEvent(
    'Online Course Purchased',
    {
      ...getOnlineCourseEventProperties(onlineCourse),
      revenue: paymentDetails.total,
      currency: onlineCourse.currency,
      discount_code: discountCode,
    },
    { custIO: true },
  );
  trackPurchase({
    friendlyName: getOcLabel(onlineCourse),
    productType: 'online-course',
    productId: `oc-${onlineCourse.id}`,
    txnId: `ocu-${onlineCourseUser.id}`,
    total: paymentDetails.total,
    tax: paymentDetails.tax,
    currency: onlineCourse.currency,
    discountCode,
  });
  // gtag('event', 'purchase', {
  //   items: [eecOnlineCourse(onlineCourse, onlineCourseUser.package_type, discountCode)],
  // });
}

export function onlineCourseRefunded(onlineCourseUser, amount) {
  trackCustomEvent('Online Course Refunded', { online_course_id: onlineCourseUser.online_course_id, amount });
  gtag('event', 'refund', {
    transaction_id: `T${onlineCourseUser.id}`,
    value: amount,
  });
}

export function onlineCourseInstructorIntroViewed(onlineCourse) {
  trackCustomEvent('Online Course Instructor Intro Viewed', getOnlineCourseEventProperties(onlineCourse));
}

export function onlineCourseFreeRegistered(onlineCourse) {
  trackCustomEvent('Online Course Free Registered', getOnlineCourseEventProperties(onlineCourse), { custIO: true });
}
export function onlineCourseFreeUnregistered(onlineCourse) {
  trackCustomEvent('Online Course Free Unregistered', getOnlineCourseEventProperties(onlineCourse));
}
export function onlineCourseNotificationRequested(onlineCourse) {
  trackCustomEvent('Online Course Notification Requested', getOnlineCourseEventProperties(onlineCourse));
}

export function onlineCourseDiscountCodeSurveyViewed(onlineCourse) {
  trackCustomEvent('Online Course Discount Code Survey Viewed', getOnlineCourseEventProperties(onlineCourse));
}
export function onlineCourseDiscountCodeSurveyCompleted(onlineCourse, funnel) {
  trackCustomEvent('Online Course Discount Code Survey Completed', {
    ...getOnlineCourseEventProperties(onlineCourse),
    survey_field_funnel: funnel,
  });
}

/************************************
 *    ONLINE COURSE EXPERIENCE      *
 ************************************/

export function onlineCourseDashboardViewed(onlineCourse) {
  trackCustomEvent('Online Course Dashboard Viewed', getOnlineCourseEventProperties(onlineCourse));
}

export function onlineCourseViewed(onlineCourse) {
  trackCustomEvent('Online Course Viewed', getOnlineCourseEventProperties(onlineCourse));
  fbevents('track', 'ViewContent', {
    content_ids: [`OC_${onlineCourse.id}`],
    content_type: 'product',
    friendly_name: getOcLabel(onlineCourse),
    value: onlineCourse.price,
    currency: 'CAD',
    name: onlineCourse.name,
    course_code: onlineCourse.courseCode,
    school_name: onlineCourse.schoolName,
    course_id: onlineCourse.courseId,
    school_id: onlineCourse.schoolId,
  });
}

export function onlineCourseTourButtonClicked(oc) {
  trackCustomEvent('Online Course Tour Button Clicked', getOnlineCourseEventProperties(oc));
}

export const onlineCourseCurriculumSearched = debounce((query, oc, course) => {
  trackCustomEvent('Online Course Curriculum Searched', {
    ...getOnlineCourseEventProperties(oc),
    ...getCourseEventProperties(course),
    query,
  });
}, 3000);

export function courseCurriculumChapterClicked(trackingProps) {
  trackCustomEvent('Course Curriculum Chapter Clicked', trackingProps);
}

export function courseCurriculumSectionClicked(trackingProps) {
  trackCustomEvent('Course Curriculum Section Clicked', trackingProps);
}

export function courseCurriculumActivityClicked(trackingProps) {
  trackCustomEvent('Course Curriculum Activity Clicked', trackingProps);
}

export function courseCurriculumFavoriteActivityClicked(trackingProps) {
  trackCustomEvent('Course Curriculum Favorite Activity Clicked', trackingProps);
}

export function courseCurriculumActivityFavorited(ocActivity, trackingProps) {
  trackCustomEvent(
    'Course Curriculum Activity Favorited',
    { ...getOcActivityProperties(ocActivity), ...trackingProps },
    { custIO: true },
  );
}

export function onlineCourseShareActivityButtonClicked(oc) {
  trackCustomEvent('Online Course Share Activity Button Clicked', getOnlineCourseEventProperties(oc));
}

export function onlineCourseShareActivityUrlCopied(oc) {
  trackCustomEvent('Online Course Share Activity URL Copied', getOnlineCourseEventProperties(oc));
}

export function onlineCourseQuestionAsked(properties) {
  trackCustomEvent('Online Course Question Asked', properties, { custIO: true, intercom: true });
}

export function onlineCourseQuestionReplySubmitted(properties) {
  trackCustomEvent('Online Course Question Reply Submitted', properties);
}

export function onlineCourseQuestionAiResponseReviewed(properties) {
  trackCustomEvent('Online Course Question AI Response Reviewed', properties);
}

export function onlineCourseQuestionEscalated(properties) {
  trackCustomEvent('Online Course Question Escalated', properties);
}

// What the events about an AI Tutor answer say about it, its question and the page's course. The page names
// the course, as it does for "Online Course Question Asked": a student's own question from another offering
// of a shared activity is listed there too, and its course goes in `question_online_course_id`.
export function getOcQuestionAiReplyProperties(question, reply, courseProperties) {
  const aiReplies = question.replies.filter(r => r.user_type === 'ai_tutor');
  return {
    ...courseProperties,
    online_course_id: courseProperties?.online_course_id ?? question.online_course_id,
    question_online_course_id: question.online_course_id,
    reply_id: reply?.id,
    ai_reply_number: reply ? aiReplies.filter(r => r.created_at <= reply.created_at).length : 0,
    model: reply?.model_id,
    provider: reply?.provider,
    question_id: question.id,
    question_status: question.status,
    escalated: Boolean(question.escalated_at),
    activity_id: question.questionable_id,
    activity_type: question.questionable_type,
    quiz_question_id: question.quiz_question_id,
    sub_question_id: question.sub_question_id,
    question_user_id: question.user_id,
  };
}

export function onlineCourseActivityViewed(canView, extraProps) {
  trackCustomEvent(
    'Online Course Activity Viewed',
    {
      ...extraProps,
      access_mode: canView ? 'full' : 'preview',
    },
    { custIO: true },
  );
}

export function onlineCourseActivityCompleted(duration, canView, extraProps) {
  trackCustomEvent('Online Course Activity Completed', {
    ...extraProps,
    time_to_complete: duration,
    access_mode: canView ? 'full' : 'preview',
  });
}

export function onlineCourseActivityDurationViewed(duration, canView, extraProps, { beacon } = {}) {
  trackCustomEvent(
    'Online Course Activity Duration Viewed',
    {
      ...extraProps,
      duration: duration,
      access_mode: canView ? 'full' : 'preview',
    },
    { beacon },
  );
}

export function onlineCourseGatedContentCTAClicked(buttonText, extraProps) {
  trackCustomEvent('Online Course Gated Content CTA Clicked', {
    ...extraProps,
    button_text: buttonText,
  });
}

export function onlineCourseActivityTokenPurchased(trackingProps) {
  trackCustomEvent('Online Course Activity Token Purchased', trackingProps);
}
export function onlineCoursPracticeQuestionTokenPurchased(trackingProps) {
  trackCustomEvent('Online Course Practice Question Token Purchased', trackingProps);
}

export function onlineCoursePracticeQuestionAttempted(isEnabled, submissionResult, skipped, attemptCount, extraProps) {
  trackCustomEvent('Online Course Practice Question Attempted', {
    ...extraProps,
    was_blocked: !isEnabled,
    quiz_question_attempt_points: submissionResult?.points,
    quiz_question_attempt_total: submissionResult?.total,
    quiz_question_attempt_score: submissionResult?.score,
    quiz_question_attempt_skipped: skipped,
    quiz_question_attempt_count: attemptCount,
  });
}

export function onlineCourseQuizQuestionLinkedSectionClicked(trackingProps) {
  trackCustomEvent('Online Course Quiz Question Linked Section Clicked', trackingProps);
}

export function getOcActivityProperties(ocActivity) {
  return (
    ocActivity && {
      activity_type: ocActivity.activity_type,
      activity_id: ocActivity.activity_id,
      oc_activity_id: ocActivity.id,
      activity_subtype:
        ocActivity.activity_metadata?.question_type ||
        ocActivity.activity_metadata?.lesson_type ||
        ocActivity.activity_metadata?.quiz_type,
      video_length: ocActivity.activity_metadata?.duration,
    }
  );
}

/************************************
 *         OC PRACTICE MODE         *
 ************************************/

export function onlineCoursePracticeModeQuestionSwapped(oc, question, buttonPos) {
  trackCustomEvent('Online Course Practice Mode Question Swapped', {
    ...getOnlineCourseEventProperties(oc),
    instructor_id: question?.user?.id,
    instructor_name: question?.user?.name,
    activity_type: 'QuizQuestion',
    activity_id: question?.id,
    activity_subtype: question?.type,
    video_length: question?.video?.duration,
    button_position: buttonPos,
  });
}

/************************************
 *              VIDEO               *
 ************************************/
export function videoPlayed(videoId, segments, length, extraProps, { beacon } = {}) {
  const watched = segments.map(([start, end]) => [Math.round(start), Math.round(end)]);
  const duration = Math.round(segments.reduce((total, [start, end]) => total + end - start, 0));
  trackCustomEvent(
    'Video Played',
    {
      video_id: videoId,
      video_play_duration: duration,
      video_play_start: watched[0][0],
      video_play_end: watched.at(-1)[1],
      video_segments: watched,
      video_length: length,
      value: duration,
      ...extraProps,
    },
    { beacon },
  );
}

export function videoCheckpointHit(videoId, checkpoint, extraProps) {
  trackCustomEvent('Video Checkpoint Hit', {
    video_id: videoId,
    ...extraProps,
    value: checkpoint,
    checkpoint,
  });
}

function eecOnlineCourse(oc, packageType = 'pro', discountCode) {
  const [price] = getPackagePricing(oc, packageType);
  return {
    item_id: `OC_${oc.id}`,
    item_name: `(${oc.school_name}) ${oc.course_code}: ${oc.name}`,
    item_brand: oc.instructor_id,
    item_category: `${oc.school_name}/${oc.course_code}/${oc.course_type}/${oc.name}`,
    item_variant: packageType,
    price,
    coupon: discountCode,
    currency: oc.currency,
    quantity: 1,
  };
}

function getOcLabel(oc) {
  return `(${oc.school_name || oc.course?.school_name || oc.course?.school?.name}) ${
    oc.course_code || oc.course?.code
  }: ${oc.name}`;
}

export function getOnlineCourseEventProperties(oc) {
  return {
    online_course_id: oc.id,
    online_course_name: oc.name,
    online_course_price: oc.price,
    online_course_status: oc.status,
    online_course_purchase_type: oc.purchase_type,
    online_course_type: oc.course_type,
    course_code: oc.course_code || oc.course?.code,
    course_id: oc.course_id,
    school_name: oc.school_name || oc.course?.school_name || oc.course?.school?.name,
    school_id: oc.school_id || oc.course?.school_id,
    instructor_id: oc.instructor_id,
    instructor_name: oc.instructor_name,
    is_subscription_offering: isSubscriptionOffering(oc),

    label: getOcLabel(oc),
  };
}

export function getCourseEventProperties(course) {
  return (
    course && {
      course_id: course.id,
      course_code: course.code,
      school_id: course.school_id,
      school_name: course.school_name || course.school?.name,
    }
  );
}

/********************************
 *  STUDY SESSION / LIVE PREP   *
 ********************************/
export function studySessionFreeEnrolled(studySession) {
  trackCustomEvent('Study Session Free Enrolled', studySessionTrackingProps(studySession), {
    custIO: {
      study_session_dates: studySession.dates.map(d => ({
        id: d.id,
        start: d.start_time && Math.floor(new Date(d.start_time).getTime() / 1000),
        end: d.end_time && Math.floor(new Date(d.end_time) / 100),
      })),
      study_session_title: studySession.title,
    },
    intercom: true,
    fb: true,
  });
  dataLayer.push({ event: 'study_session_free_enrolled', product_type: 'StudySession', product_id: studySession.id });
  oaiq('measure', 'registration_completed', { type: 'customer_action' });
}

export function studySessionCheckoutStepViewed(studySession, step, currency) {
  trackCustomEvent('Study Session Checkout Step Viewed', {
    ...studySessionTrackingProps(studySession),
    checkout_step: step,
  });
  if (step === 'payment') {
    fbevents('track', 'InitiateCheckout', {
      content_category: 'study-session',
      currency,
      value: studySession.price,
    });
    gtag('event', 'begin_checkout', {
      currency,
      value: studySession.price,
      items: [
        {
          item_id: `SS_${studySession.id}`,
          item_name: [studySession.course?.code, studySession.title].filter(Boolean).join(': '),
          price: studySession.price,
          currency,
        },
      ],
    });
    CustomerIO.track('product_checkout_viewed', {
      product_id: `SS${studySession.id}`,
      product_type: 'study-session',
      product_price: studySession.price,
      product_name: [studySession.course?.code, studySession.title].filter(Boolean).join(': '),
      study_session_type: studySession.type,
    });
  }
}

export function studySessionPurchased(studySession, studySessionUser, { currency, discountCode, total: amount }) {
  trackCustomEvent(
    'Study Session Purchased',
    {
      ...studySessionTrackingProps(studySession),
      revenue: amount,
      currency,
      discount_code: discountCode?.code,
    },
    {
      custIO: {
        study_session_dates: studySession?.dates.map(d => ({
          id: d.id,
          start: d.start_time && Math.floor(new Date(d.start_time).getTime() / 1000),
          end: d.end_time && Math.floor(new Date(d.end_time) / 100),
        })),
        study_session_title: studySession?.title,
      },
    },
  );
  trackPurchase({
    friendlyName: [studySession.course?.code, studySession.title].filter(Boolean).join(': '),
    productType: 'study-session',
    productId: `ss-${studySession.id}`,
    txnId: `ssu-${studySessionUser.id}`,
    total: amount,
    currency,
    discountCode,
    price: studySession.price,
  });
}

export function studySessionBundlePurchased(bundle, bundleUser, { currency, discountCode, total: amount }) {
  trackCustomEvent(
    'Study Session Bundle Purchased',
    {
      study_session_bundle_id: bundle.id,
      study_session_bundle_title: bundle.title,
      course_id: bundle.course?.id,
      course_code: bundle.course?.code,
      study_sessions_count: bundle.study_sessions?.length,
      includes_free_session: bundle.study_sessions?.some(ss => !ss.price),
      study_sessions: bundle.study_sessions?.map(ss => ({
        id: ss.id,
        title: ss.title,
        type: ss.type,
        price: ss.price,
      })),
      revenue: amount,
      currency,
      discount_code: discountCode?.code,
    },
    { custIO: true },
  );
  trackPurchase({
    friendlyName: [bundle.course?.code, bundle.title].filter(Boolean).join(': '),
    productType: 'study-session-bundle',
    productId: `ssb-${bundle.id}`,
    txnId: `ssbu-${bundleUser.id}`,
    total: amount,
    currency,
    discountCode,
    price: bundle.price,
  });
}

export function studySessionCalendarLinkClicked(studySession, calendar) {
  trackCustomEvent('Study Session Calendar Link Clicked', {
    calendar: calendar,
    ...studySessionTrackingProps(studySession),
  });
}

export function studySessionJoinNowClicked(studySession) {
  trackCustomEvent('Study Session Join Now Clicked', studySessionTrackingProps(studySession));
}
export function studySessionAttachmentViewed(studySession, attachment) {
  trackCustomEvent('Study Session Attachment Viewed', {
    ...studySessionTrackingProps(studySession),
    attachment_type: attachment.type,
    attachment_id: attachment.id,
    attachment_name: attachment.name,
  });
}
export function studySessionRecordingViewed(studySession, recording) {
  trackCustomEvent('Study Session Recording Viewed', {
    ...studySessionTrackingProps(studySession),
    recording_id: recording.id,
    recording_title: recording.name,
  });
}

export function studySessionTrackingProps(ss) {
  return (
    ss && {
      study_session_id: ss.id,
      study_session_type: ss.type,
      study_session_mcat_type: ss.mcat_type,
      study_session_mcat_subtype: ss.mcat_subtype,
      study_session_purchase_type: ss.purchase_type,
      course_id: ss.course?.id,
      course_code: ss.course?.code,
      school_name: ss.course?.school?.name,
      school_id: ss.course?.school_id,
      course_ids: ss.course_ids,
      course_codes: ss.courses?.map(c => c.code),
      school_names: ss.courses?.map(c => c.school?.name),
      school_ids: ss.courses?.map(c => c.school_id),
    }
  );
}

/********************************
 *        TUTOR SEARCH          *
 ********************************/

export function tutorLandingPageViewed() {
  trackCustomEvent('Tutor Landing Page Viewed');
}

export function tutorSearchPageResultsViewed(subject, sortOption, count) {
  trackCustomEvent('Tutor Search Page Results Viewed', {
    subject,
    sorting_option: sortOption,
    tutor_count: count,
  });
}

export function tutorLandingSeeMoreLinkClicked() {
  trackCustomEvent('Tutor Landing See More Link Clicked');
}
export function tutorLandingSubjectLinkClicked(subject) {
  trackCustomEvent('Tutor Landing Subject Link Clicked', { subject });
}

export function tutorSearchSubjectSelected(subject) {
  trackCustomEvent('Tutor Search Subject Selected', { subject });
}

export function tutorSearchSortingOptionSelected(option) {
  trackCustomEvent('Tutor Search Sorting Option Selected', { sorting_option: option });
}

export function tutorSearchSeeMoreButtonClicked() {
  trackCustomEvent('See More Results Clicked');
}

export function tutorSearchResultViewProfileClicked(tutor) {
  trackCustomEvent('Search Result View Profile Clicked', { tutor_name: tutor });
}

export function tutorSearchResultSubjectSelected(subject, tutor) {
  trackCustomEvent('Search Result Subject Selected', { subject, tutor_name: tutor });
}

export function tutorSearchResultSubjectDeselected(subject, tutor) {
  trackCustomEvent('Search Result Subject Deselected', { subject, tutor_name: tutor });
}

export function tutorSearchResultBookALessonClicked(tutor) {
  trackCustomEvent('Search Result Book a Lesson Clicked', { tutor_name: tutor });
}

export function tutorSearchResultMessageButtonClicked(tutor) {
  trackCustomEvent('Tutor Search Result Message Button Clicked', { tutor_name: tutor });
}

export function tutorSearchResultConsultationButtonClicked(tutor) {
  trackCustomEvent('Tutor Search Result Consultation Button Clicked', { tutor_name: tutor });
}

export function tutorSearchMessageSent(tutor) {
  trackCustomEvent('Tutor Search Chat Message Sent', { tutor_name: tutor });
}

/********************************
 *     TUTOR BOOKING DIALOG     *
 ********************************/

export function tutorBookingChangeStepButtonClicked(step) {
  trackCustomEvent('Tutor Booking Checkout Step Changed', { step });
}

export function tutorBookingCheckoutStepViewed(stepIdx, stepName, tutorRate) {
  trackCustomEvent('Tutor Booking Checkout Step Viewed', {
    checkout_step: stepIdx,
    checkout_step_name: stepName,
    tutor_rate: tutorRate,
  });
}

/********************************
 *      STUDENT DASHBOARD       *
 ********************************/

export function studentDashboardViewed() {
  trackCustomEvent('Student Dashboard Viewed');
}

// `source` is what the student pressed: the composer, or one of the starters under it.
export function studentDashboardAiPromptSent(props) {
  trackCustomEvent('Student Dashboard AI Prompt Sent', props);
}

export function dashboardEnrolledOnlineCourseCardClicked(onlineCourse) {
  trackCustomEvent('Dashboard Enrolled Online Course Card Clicked', getOnlineCourseEventProperties(onlineCourse));
}
export function dashboardRecommendedOnlineCourseCardClicked(onlineCourse) {
  trackCustomEvent('Dashboard Recommended Online Course Card Clicked', getOnlineCourseEventProperties(onlineCourse));
}

export function instagramWidgetClicked() {
  trackCustomEvent('Instagram Widget Clicked');
}

/********************************
 *   STUDENT ACCOUNT SETTINGS   *
 ********************************/

/********************************
 *        LANDING PAGES         *
 ********************************/

export function homepageViewed() {
  trackCustomEvent('Homepage Viewed');
}

export function universityLandingPageViewed() {
  trackCustomEvent('University Landing Page Viewed');
}
export function highSchoolLandingPageViewed() {
  trackCustomEvent('High School Landing Page Viewed');
}
export function educatorsLandingPageViewed() {
  trackCustomEvent('Educators Landing Page Viewed');
}
export function parentsLandingPageViewed() {
  trackCustomEvent('Parents Landing Page Viewed');
}

export function medSchoolCalculatorViewed() {
  trackCustomEvent('Med School Calculator Viewed');
}
export function medSchoolCalculatorFiltered(filter) {
  trackCustomEvent('Med School Calculator Filtered', filter);
}

export function onDemandLandingPageViewed() {
  trackCustomEvent('On-Demand Landing Page Viewed');
}
export function livePrepLandingPageViewed() {
  trackCustomEvent('Live Prep Landing Page Viewed');
}

export function seeMoreProfsButtonClicked() {
  trackCustomEvent('See More Profs Button Clicked');
}

export function landingPageBookAChatClicked(chatTarget) {
  trackCustomEvent('Landing Page Book a Chat Clicked', { chat_target: chatTarget });
  dataLayer.push({ event: 'book_a_chat', chat_target: chatTarget });
}
export function schoolsPageViewed() {
  trackCustomEvent('Schools Page Viewed');
}

export function schoolPageViewed(school) {
  trackCustomEvent('School Page Viewed', { school_id: school.id, school_name: school.name });
}

export function promoLandingPageViewed() {
  trackCustomEvent('Promo Landing Page Viewed');
}

export function homepageProductClicked(product) {
  trackCustomEvent('Home Page Product Clicked', { product });
}

export function instructorCardClicked(instructor) {
  trackCustomEvent('Instructor Card Clicked', {
    instructor_id: instructor.id,
    instructor_name: instructor.name,
  });
}

export function landingPageCTAClicked(cta_section, cta_id, cta_sub_id) {
  trackCustomEvent('Landing Page CTA Clicked', { cta_id, cta_section, cta_sub_id });
}

export function landingPageSchoolTypeFilterToggled(school_type) {
  trackCustomEvent('Landing Page School Type Filter Toggled', { school_type });
}

export function homepageSearchSchoolSearched(query) {
  trackCustomEvent('Homepage Search School Searched', { query });
}
export function homepageSearchSchoolSelected(schoolId, schoolName, schoolType) {
  trackCustomEvent('Homepage Search School Selected', {
    school_id: schoolId,
    school_name: schoolName,
    school_type: schoolType,
  });
}
export function homepageSearchCourseSearched(query, inputType, schoolId, schoolName, schoolType) {
  trackCustomEvent('Homepage Search Course Searched', {
    query,
    input_type: inputType,
    school_id: schoolId,
    school_name: schoolName,
    school_type: schoolType,
  });
}
export function homepageSearchCourseSelected(inputType, schoolType, courseCode, courseId) {
  trackCustomEvent('Homepage Search Course Selected', {
    input_type: inputType,
    school_type: schoolType,
    course_code: courseCode,
    course_id: courseId,
  });
}

export function wizeWriterLandingPageViewed() {
  trackCustomEvent('Wize Writer Landing Page Viewed');
}
export function wizeWriterDashboardViewed() {
  trackCustomEvent('Wize Writer Dashboard Viewed');
}
export function wizeWriterEssaySaved() {
  trackCustomEvent('Wize Writer Essay Saved');
}

export function mcatLandingPageViewed(variant) {
  trackCustomEvent('MCAT Landing Page Viewed', { page_variant: variant }, { custIO: true });
  tikTokTrack('ViewContent', {
    content_type: 'product',
    content_id: 'MCAT_ELITE_515',
  });
}

export function mcatPurchased({ currency, discountCode, payment_option, textbookOption, total, userCourse }) {
  const isOnDemand = !userCourse.course_schedule_id;
  const productId = isOnDemand ? 'mcat-self-paced' : 'mcat-elite-515';
  trackCustomEvent(
    'MCAT Purchased',
    {
      product_id: productId,
      payment_option,
      revenue: total,
      currency,
      discount_code: discountCode?.code,
      textbook_option: textbookOption,
    },
    { custIO: true },
  );
  trackPurchase({
    friendlyName: isOnDemand ? 'MCAT Self-Paced' : 'MCAT Elite 515',
    productType: 'mcat',
    productId,
    txnId: `mcat-${userCourse.id}`,
    total,
    currency,
    discountCode,
  });
}

export function lsatPurchased({ currency, discountCode, payment_option, textbookOption, total, userCourse }) {
  const isOnDemand = !userCourse.course_schedule_id;
  const productId = isOnDemand ? 'lsat-self-paced' : 'lsat-elite-170';
  trackCustomEvent(
    'LSAT Purchased',
    {
      product_id: productId,
      payment_option,
      revenue: total,
      currency,
      discount_code: discountCode?.code,
      textbook_option: textbookOption,
    },
    { custIO: true },
  );
  trackPurchase({
    friendlyName: isOnDemand ? 'LSAT Self-Paced' : 'LSAT Elite 170',
    productType: 'lsat',
    productId,
    txnId: `lsat-${userCourse.id}`,
    total,
    currency,
    discountCode,
  });
}
export function datPurchased({ currency, discountCode, payment_option, textbookOption, total, userCourse }) {
  const isOnDemand = !userCourse.course_schedule_id;
  const productId = isOnDemand ? 'dat-self-paced' : 'dat-elite';
  trackCustomEvent(
    'DAT Purchased',
    {
      product_id: productId,
      payment_option,
      revenue: total,
      currency,
      discount_code: discountCode?.code,
      textbook_option: textbookOption,
    },
    { custIO: true },
  );
  trackPurchase({
    friendlyName: isOnDemand ? 'DAT Self-Paced' : 'DAT Elite',
    productType: 'dat',
    productId,
    txnId: `dat-${userCourse.id}`,
    total,
    currency,
    discountCode,
  });
}

export function stdCoursePurchased({
  courseSlug,
  currency,
  discountCode,
  payment_option,
  textbookOption,
  total,
  userCourse,
}) {
  const isOnDemand = !userCourse.course_schedule_id;
  const slug = courseSlug.toUpperCase();
  const productId = `${slug}-${isOnDemand ? 'self-paced' : 'live'}`;
  trackCustomEvent(
    `${slug} Purchased`,
    {
      product_id: productId,
      payment_option,
      revenue: total,
      currency,
      discount_code: discountCode?.code,
      textbook_option: textbookOption,
    },
    { custIO: true },
  );
  trackPurchase({
    friendlyName: isOnDemand ? `${slug} Self-Paced` : `${slug} Live`,
    productType: courseSlug,
    productId,
    txnId: `${courseSlug}-${userCourse.id}`,
    total,
    currency,
    discountCode,
  });
}

export function customProductPurchased(productUser, customProduct) {
  const total = productUser.amount_paid;
  const currency = customProduct.currency?.toUpperCase();

  const bundledLabels = Object.entries(customProduct.products_config || {})
    .filter(([, v]) => v?.enabled)
    .map(([key]) => key)
    .filter(Boolean);
  const productDescription =
    bundledLabels.length ? `${customProduct.title} (${bundledLabels.join(', ')})` : customProduct.title;

  trackCustomEvent(
    'Custom Product Purchased',
    {
      product_id: customProduct.id,
      product_title: customProduct.title,
      product_category: customProduct.product_category,
      revenue: total,
      currency,
    },
    { custIO: true },
  );
  trackPurchase({
    friendlyName: customProduct.title,
    productType: 'custom-product',
    productId: `cp-${customProduct.id}`,
    txnId: `cpu-${productUser.id}`,
    price: customProduct.price,
    total,
    currency,
    productDescription,
  });
}

function currencyAndAmountInCents(amount, currency) {
  if (!Number.isFinite(amount) || !currency) return {};
  return { amount: Math.round(amount * 100), currency: currency.toUpperCase() };
}

function trackPurchase({
  currency,
  discountCode,
  friendlyName,
  price,
  productDescription,
  productId,
  productType,
  tax,
  total,
  txnId,
}) {
  Intercom('trackEvent', `purchase-${productType}`, {
    product_id: productId,
    product_name: friendlyName,
    price: currencyAndAmountInCents(total, currency),
  });
  fbevents('track', 'Purchase', {
    content_ids: [productId],
    content_type: 'product',
    content_name: friendlyName,
    value: total,
    currency,
  });
  gtag('event', 'purchase', {
    transaction_id: txnId,
    currency,
    value: total,
    coupon: discountCode?.code || discountCode,
    tax,
    items: [
      {
        item_id: productId,
        item_name: friendlyName,
        price: price ?? total,
        currency,
        quantity: 1,
      },
    ],
  });
  ShareASale.purchase({
    orderId: txnId,
    productId: productId,
    amount: total,
    currency,
    discountCode: discountCode?.code || discountCode,
  });
  tikTokTrack('CompletePayment', {
    content_type: 'product',
    content_id: productId,
    value: total,
    currency,
    quantity: 1,
    price: price ?? total,
  });
  oaiq(
    'measure',
    'order_created',
    {
      type: 'contents',
      ...currencyAndAmountInCents(total, currency),
      contents: [
        {
          id: productId,
          name: productDescription || friendlyName,
          content_type: productType,
          quantity: 1,
          ...currencyAndAmountInCents(price ?? total, currency),
        },
      ],
    },
    { event_id: txnId },
  );
  dataLayer.push({
    event: 'purchase',
    txn_id: txnId,
    currency,
    value: total,
    product_type: productType,
    product_id: productId,
    ...(productDescription && { product_description: productDescription }),
  });
}

export function builderLandingPageViewed(pageId, pageRevision, pageName) {
  trackCustomEvent('Builder.io Landing Page Viewed', { page_id: pageId, page_rev: pageRevision, page_name: pageName });
}

export function courseTextbookViewed(courseSlug, booklet, isSample) {
  trackCustomEvent('Course Textbook Viewed', { course_slug: courseSlug, booklet, is_sample: isSample });
}

/********************************
 *        SUBSCRIPTIONS         *
 ********************************/

export function subscriptionLandingPageViewed() {
  trackCustomEvent('Subscription Landing Page Viewed');
}

export function subscriptionStartTrialButtonClicked(countryCode, trackingProps) {
  trackCustomEvent('Subscription Start Trial Button Clicked', trackingProps, {
    custIO: true,
    intercom: true,
  });
  fbevents('track', 'StartTrial', {
    currency: countryCode === 'CA' ? 'CAD' : 'USD',
    value: SUBSCRIPTION_PRICE,
  });
  gtag('event', 'start_trial');
}

export function subscriptionCheckoutDialogViewed(countryCode, trackingProps) {
  trackCustomEvent('Subscription Checkout Dialog Viewed', trackingProps, { custIO: true });
  const currency = countryCode === 'CA' ? 'CAD' : 'USD';
  fbevents('track', 'InitiateCheckout', {
    content_category: 'subscription',
    currency,
    value: SUBSCRIPTION_PRICE,
  });
  gtag('event', 'begin_checkout', {
    currency,
    value: SUBSCRIPTION_PRICE,
    items: [{ item_id: 'SUBSCRIPTION', item_name: 'Wize Subscription', price: SUBSCRIPTION_PRICE, currency }],
  });
  CustomerIO.track('product_checkout_viewed', {
    product_id: 'subscription',
    product_type: 'subscription',
    product_price: SUBSCRIPTION_PRICE,
    product_name: 'Wizeprep Plus',
  });
}
export function subscriptionCheckoutCompleted(
  subscription,
  countryCode,
  trackingProps,
  total,
  discountCode,
  bogoEnabled,
) {
  const currency = countryCode === 'CA' ? 'CAD' : 'USD';
  const txnId = `SUB_${subscription.id}`;
  trackCustomEvent(
    'Subscription Checkout Completed',
    {
      revenue: total,
      currency,
      ...trackingProps,
      billing_interval: subscription.billing_interval,
      discount_code: discountCode?.code,
      free_trial: !!discountCode?.subscription_trial,
      stripe_subscription_id: subscription.stripe_subscription_id,
      bogo: bogoEnabled,
      is_premed_plus: subscription.is_premed_plus,
    },
    { custIO: true, intercom: true },
  );
  fbevents('track', 'Purchase', {
    content_ids: ['SUBSCRIPTION'],
    content_type: 'product',
    content_name: 'Wize Subscription',
    currency,
    value: total,
  });
  gtag('event', 'purchase', {
    transaction_id: txnId,
    currency,
    value: total,
    coupon: discountCode?.code,
    items: [
      {
        item_id: 'SUBSCRIPTION',
        item_name: 'Wize Subscription',
        price: total,
        currency,
        coupon: discountCode?.code,
        quantity: 1,
      },
    ],
  });
  ShareASale.purchase({
    orderId: txnId,
    productId: 'SUBSCRIPTION',
    amount: total,
    currency,
    discountCode: discountCode?.code,
  });
  tikTokTrack('Subscribe', {
    content_type: 'product',
    content_id: subscription.billing_interval === 'yearly' ? 'subscription-yearly' : 'subscription',
    value: total,
    currency,
  });
  oaiq(
    'measure',
    'subscription_created',
    { type: 'plan_enrollment', plan_id: subscription.billing_interval, ...currencyAndAmountInCents(total, currency) },
    { event_id: txnId },
  );
  dataLayer.push({
    event: 'purchase',
    txn_id: txnId,
    value: total,
    currency,
    product_type: 'Subscription',
    product_id: subscription.billing_interval,
  });
}

export function subscriptionCanceled(reasonProps) {
  trackCustomEvent('Subscription Canceled', { cancellation_reason_key: reasonProps?.reason_key }, { custIO: true });
}
export function subscriptionUncanceled() {
  trackCustomEvent('Subscription Uncanceled', undefined, { custIO: true });
}
export function subscriptionUnpaused() {
  trackCustomEvent('Subscription Unpaused', undefined, { custIO: true });
}

/********************************
 *        COURSE PURCHASE       *
 ********************************/

export function courseCheckoutStepViewed(course, step) {
  trackCustomEvent('Course Checkout Step Viewed', {
    course_code: course.code,
    course_id: course.id,
    school_name: course.school_name,
    school_id: course.school_id,
    checkout_step: step,
  });
  if (step === 'payment') {
    fbevents('track', 'InitiateCheckout', {
      content_category: 'course',
      currency: course.currency,
      value: course.price,
    });
    gtag('event', 'begin_checkout', {
      currency: course.currency,
      value: course.price,
      items: [
        {
          item_id: `C_${course.id}`,
          item_name: [course.school_name, course.code].filter(Boolean).join(': '),
          price: course.price,
          currency: course.currency,
        },
      ],
    });
    CustomerIO.track('product_checkout_viewed', {
      product_id: `C${course.id}`,
      product_type: 'course',
      product_price: course.price,
      product_name: course.code,
    });
  }
}

export function coursePurchaseFailed(course, error) {
  trackCustomEvent('Course Purchase Failed', {
    type: error.type,
    code: error.code,
    message: error.message,
    decline_code: error.decline_code,
  });
  CustomerIO.track('product_checkout_failed', {
    product_id: `C${course.id}`,
    product_type:
      isMcatCourse(course) ? 'mcat-self-paced'
      : isLsatCourse(course) ? 'lsat-self-paced'
      : 'course',
    product_price: course.price,
    product_name:
      isMcatCourse(course) ? 'MCAT Self-Paced'
      : isLsatCourse(course) ? 'LSAT Self-Paced'
      : course.code,
    error_type: error.type,
    error_message: error.message,
  });
}

export function coursePurchased(course, userCourse, subscription, { currency, discountCode, total } = {}) {
  trackCustomEvent(
    'Course Purchased',
    {
      course_id: course.id,
      course_code: course.code,
      school_name: course.school_name,
      school_id: course.school_id,
      revenue: total,
      currency,
      discount_code: discountCode?.code,
    },
    { custIO: true },
  );
  trackPurchase({
    friendlyName: [course.school_name, course.code].filter(Boolean).join(': '),
    productType: 'course',
    productId: `course-${course.id}`,
    txnId: subscription ? `course-sub-${subscription.id}` : `uc-${userCourse.id}`,
    currency,
    discountCode,
    price: course.price,
    total,
  });
}

/********************************
 *        TEXTBOOK PAGES        *
 ********************************/
export function textbookSubjectsPageViewed(level) {
  trackCustomEvent('Textbook Subjects Page Viewed', { level });
}
export function textbookSubjectPageViewed(level, subject) {
  trackCustomEvent('Textbook Subject Page Viewed', { subject, level });
}
export function textbookSectionPageViewed(oc, section) {
  trackCustomEvent('Textbook Section Page Viewed', {
    subject: oc.subject,
    level: oc.textbook_level,
    online_course_id: oc.id,
    online_course_name: oc.name,
    section_id: section.id,
    section_title: section.title,
    chapter_id: section.chapter?.id,
    chapter_title: section.chapter?.title,
  });
}

/********************************
 *             BLOG             *
 ********************************/
function blogPostMeta(post) {
  return { blog_post_id: post.id, blog_post_title: post.title };
}

export function blogHomeViewed() {
  trackCustomEvent('Blog Home Viewed');
}

export function blogPostViewed(post) {
  trackCustomEvent('Blog Post Viewed', blogPostMeta(post));
}
export function blogPostShareButtonClicked(post) {
  trackCustomEvent('Blog Post Share Button Clicked', blogPostMeta(post));
}
export function blogPostPreviousLinkClicked(post) {
  trackCustomEvent('Blog Post Previous Link Clicked', blogPostMeta(post));
}
export function blogPostNextLinkClicked(post) {
  trackCustomEvent('Blog Post Next Link Clicked', blogPostMeta(post));
}
export function blogPostLiked(post) {
  trackCustomEvent('Blog Post Liked', blogPostMeta(post));
}
export function blogPostsFilterSelected(filterName, filterValue) {
  trackCustomEvent('Blog Posts Filter Selected', { filter_name: filterName, filter_value: filterValue });
}
export function blogCarouselButtonClicked(direction) {
  trackCustomEvent('Blog Carousel Button Clicked', { button_direction: direction });
}
export function blogPaginationLinkClicked(pageNum) {
  trackCustomEvent('Blog Pagination Link Clicked', { page_num: pageNum });
}

/********************************
 *             FORUM            *
 ********************************/

export function forumPostContentEntered(props) {
  trackCustomEvent('Forum Post Content Entered', props);
}
export function forumPostCreated(props) {
  trackCustomEvent('Forum Post Created', props);
}

export function forumReplyCreated(props) {
  trackCustomEvent('Forum Reply Created', props);
}

/********************************
 *     INVITES / REFERRALS      *
 ********************************/

export function referralPageViewed(referrer) {
  trackCustomEvent('Referral Page Viewed', { referrer_id: referrer.id });
}
export function referrerPageViewed() {
  trackCustomEvent('Referrer Page Viewed');
}

export function referralActionClicked(action) {
  trackCustomEvent('Referral Action Clicked', { action_type: action });
}
export function referralRedeemCreditsClicked() {
  trackCustomEvent('Referral Redeem Credits Clicked');
}

/********************************
 *      HOMEWORK HELP PAGE      *
 ********************************/

export function homeworkHelpQuestionPageViewed() {
  trackCustomEvent('Homework Help Question Page Viewed');
}

export function homewokHelpQuestionSubmitted(subject) {
  trackCustomEvent('Homewok Help Question Submitted', { subject });
}

export function homeworkHelpQuestionLinkClicked(question) {
  trackCustomEvent('Homework Help Question Link Clicked', { question_id: question.id });
}

export function homeworkHelpResponseRated(isHelpful) {
  trackCustomEvent('Homework Help Response Rated', { is_helpful: isHelpful });
}

/********************************
 *           AI TUTOR           *
 ********************************/

export function aiTutorMessageSent(chat, messageType, props) {
  trackCustomEvent(
    'AI Tutor Message Sent',
    { model: chat?.model_id, provider: chat?.provider, message_type: messageType, ...props },
    { custIO: true },
  );
}

// Once per visit to the tutor page. `can_chat` on arrival separates a student who was free to
// try from one who had already run out of credit, whose send is the paywall event below. Guests
// need no flag of their own: the `user_type` super property already says `guest`.
export function aiTutorPageViewed(props) {
  trackCustomEvent('AI Tutor Page Viewed', props);
}

// A send the tutor refused. A guest is asked to sign up and anyone else to subscribe, which
// `user_type` already tells apart. Fired on the attempt rather than when the notice is shown, so it
// counts the students who actually wanted to go on — and it goes to Customer.io because that is
// who marketing would want to write to.
export function aiTutorPaywallHit(chat) {
  trackCustomEvent('AI Tutor Paywall Hit', { chat_id: chat?.id }, { custIO: true });
}

// How much the student put in the composer for a message: the text as sent (plain, not the
// editor's markup), and the files attached to it.
export function aiTutorMessageSizeProps(content, attachments) {
  return { text_length: content?.trim().length ?? 0, attachment_count: attachments?.length ?? 0 };
}

export function aiTutorMessageReviewed(chat, messageId, response, props) {
  trackCustomEvent('AI Tutor Message Reviewed', {
    model: chat?.model_id,
    provider: chat?.provider,
    message_id: messageId,
    chat_id: chat?.id,
    response,
    ...props,
  });
}

export function aiTutorActivityReviewed(chat, artifact, response) {
  trackCustomEvent('AI Tutor Activity Reviewed', {
    model: chat?.model_id,
    provider: chat?.provider,
    response,
    chat_id: chat?.id,
    artifact_id: artifact.id,
    activity_type: artifact.activity_type,
    activity_id: artifact.activity_id,
    activity_completed: !!artifact.completed_at,
  });
}

/********************************
 *      STUDY INITIATIVES       *
 ********************************/

// Named "Study Initiative" because the `Study Session …` events above belong to the older
// grade-booster product. Properties are ids, enums and counts only: nothing a student typed or
// uploaded.
function studyInitiativeProps(initiative) {
  return {
    study_initiative_id: initiative?.id,
    course_id: initiative?.course_id,
    goal: initiative?.goal,
    stage: initiative?.status,
  };
}

export function studyInitiativeSetupStepViewed(initiative, step) {
  trackCustomEvent('Study Initiative Setup Step Viewed', { ...studyInitiativeProps(initiative), step });
}

export function studyInitiativeSetupStepCompleted(initiative, step, props) {
  trackCustomEvent('Study Initiative Setup Step Completed', { ...studyInitiativeProps(initiative), step, ...props });
}

// A click on a button the step could not act on yet, answered with a message saying why.
export function studyInitiativeSetupBlocked(initiative, step, reason) {
  trackCustomEvent('Study Initiative Setup Blocked', { ...studyInitiativeProps(initiative), step, reason });
}

export function studyInitiativePromptStarterClicked(initiative, starterId) {
  trackCustomEvent('Study Initiative Prompt Starter Clicked', {
    ...studyInitiativeProps(initiative),
    starter: starterId,
  });
}

export function studyInitiativeDiagnosticStarted(initiative, source) {
  trackCustomEvent('Study Initiative Diagnostic Started', { ...studyInitiativeProps(initiative), source });
}

export function studyInitiativeStudyingStarted(initiative, source, props) {
  trackCustomEvent('Study Initiative Studying Started', { ...studyInitiativeProps(initiative), source, ...props });
}

export function studyInitiativeMaterialCompleted(initiative, material) {
  trackCustomEvent('Study Initiative Material Completed', {
    ...studyInitiativeProps(initiative),
    material_id: material?.id,
    activity_type: material?.activity_type,
    position: material?.position,
    material_source: material?.source,
  });
}

export function studyInitiativeWrapUpViewed(initiative, props) {
  trackCustomEvent('Study Initiative Wrap Up Viewed', { ...studyInitiativeProps(initiative), ...props });
}

export function studyInitiativeWrapUpActionClicked(initiative, action) {
  trackCustomEvent('Study Initiative Wrap Up Action Clicked', { ...studyInitiativeProps(initiative), action });
}
